import React, { KeyboardEventHandler, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useValidatableFormFieldFocus } from '../../../hooks';
import { FormFieldWrap, Textarea } from '@zooplus/ui-components';
import { useFormFieldMainValidationWithOnValueChangeValidation } from '../../../hooks';
import { TextareaWithCounterFormFieldValidator } from '../../../hooks/validation/validator/TextareaWithCounterFormFieldValidator';

const colorNormalClass = '.z-info-tag--neutral';
const colorWarningClass = 'z-info-tag--warning';

const DescriptionField: React.FC = () => {
  const { t } = useTranslation();

  const label = t('form.fields.description.label');
  const placeholder = t('form.fields.description.placeholder');
  const errorMessage = t('form.fields.description.error_message');
  const isRequired = true;
  const minLength = null;
  const maxLength = 1000;

  const [counterVisible, setCounterVisible] = useState(false);
  const [counterColorCssClass, setCounterColorCssClass] = useState(colorNormalClass);
  const [counterValue, setCounterValue] = useState(0);
  const inputWrapperElementRef = useRef<HTMLDivElement>(null);

  const [value, setValue] = useState('');
  const { hasError, validate } = useFormFieldMainValidationWithOnValueChangeValidation(
    new TextareaWithCounterFormFieldValidator(isRequired, minLength, maxLength),
    { value },
  );
  useValidatableFormFieldFocus({
    formFieldSelector: 'textarea',
    formFieldWrapperElementRef: inputWrapperElementRef,
  });

  const onKeyUpCallback: KeyboardEventHandler<HTMLTextAreaElement> = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    updateCounter(e);
  };

  const updateCounter: KeyboardEventHandler<HTMLTextAreaElement> = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    const formField = e.target as HTMLTextAreaElement;
    const len = formField.value.length;
    const colorClass = len >= formField.maxLength ? colorWarningClass : colorNormalClass;

    setCounterValue(formField.maxLength - len);
    setCounterColorCssClass(colorClass);
    setCounterVisible(true);
  };

  const onChangeCallback = useCallback(
    (evt: React.ChangeEvent<HTMLTextAreaElement>) => setValue(evt.target.value),
    [],
  );
  const onBlurCallback = useCallback(() => validate(), [validate]);

  return (
    <div
      data-test="contactForm.field.description"
      data-form-control-type="COUNTER"
      className="contactForm__field"
      ref={inputWrapperElementRef}>
      <FormFieldWrap
        labelFor="description"
        isRequired={isRequired}
        labelText={label}
        errorMessage={hasError && errorMessage}>
        <Textarea
          id="description"
          name="description"
          rows={8}
          hasError={hasError}
          placeholder={placeholder}
          maxLength={maxLength}
          onChange={onChangeCallback}
          onBlur={onBlurCallback}
          onKeyUp={onKeyUpCallback}></Textarea>
      </FormFieldWrap>

      <div className="counter">
        {counterVisible && (
          <div className={`formField__counter ${counterColorCssClass}`}>
            <span className="formField__counterValue">{counterValue}</span>/
            <span className="formField__counterMaxValue">{maxLength}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default DescriptionField;
